/** Catalogs for every personalization option. Settings UI and renderers share these. */

export interface Option<T extends string> {
  id: T;
  name: string;
  blurb: string;
}

export type ThemeId =
  'neon' | 'felt' | 'midnight' | 'paper' | 'synthwave' | 'backyard';
export const THEMES: Option<ThemeId>[] = [
  { id: 'neon', name: 'Neon', blurb: 'Arcade after dark' },
  { id: 'synthwave', name: 'Synthwave', blurb: 'Sunset over the grid' },
  { id: 'felt', name: 'Felt', blurb: 'Green baize and brass' },
  { id: 'midnight', name: 'Midnight', blurb: 'Quiet monochrome' },
  { id: 'paper', name: 'Paper', blurb: 'Scorepad and pencil' },
  { id: 'backyard', name: 'Backyard', blurb: 'Yardzee on the lawn' },
];

export type DiceThemeId =
  'neon' | 'ivory' | 'casino' | 'wood' | 'gem' | 'ink' | 'chrome';
export const DICE_THEMES: Option<DiceThemeId>[] = [
  { id: 'neon', name: 'Neon', blurb: 'Dark body, glowing pips' },
  { id: 'ivory', name: 'Ivory', blurb: 'The classic tabletop die' },
  { id: 'casino', name: 'Casino', blurb: 'Translucent red, razor edges' },
  { id: 'gem', name: 'Gem', blurb: 'Cut violet glass' },
  { id: 'wood', name: 'Wood', blurb: 'Yardzee oak, burnt pips' },
  { id: 'ink', name: 'Ink', blurb: 'White with heavy black pips' },
  { id: 'chrome', name: 'Chrome', blurb: 'Brushed metal' },
];

export type RollAnimationId = 'tumble' | 'spin' | 'shake' | 'instant';
export const ROLL_ANIMATIONS: Option<RollAnimationId>[] = [
  { id: 'tumble', name: 'Tumble', blurb: 'Dice bounce and settle' },
  { id: 'spin', name: 'Spin', blurb: 'A clean pirouette' },
  { id: 'shake', name: 'Shake', blurb: 'Rattle in place' },
  { id: 'instant', name: 'Instant', blurb: 'No animation' },
];

export type RollSpeedId = 'slow' | 'normal' | 'fast';
export const ROLL_SPEEDS: Option<RollSpeedId>[] = [
  { id: 'slow', name: 'Slow', blurb: 'Savor it' },
  { id: 'normal', name: 'Normal', blurb: '' },
  { id: 'fast', name: 'Fast', blurb: 'Snappy' },
];
/** Base roll animation duration in ms. */
export const ROLL_DURATION_MS: Record<RollSpeedId, number> = {
  slow: 1400,
  normal: 850,
  fast: 450,
};

export type CupId = 'leather' | 'chrome' | 'neon' | 'plastic' | 'none';
export const CUPS: Option<CupId>[] = [
  { id: 'neon', name: 'Neon', blurb: 'Outlined in light' },
  { id: 'leather', name: 'Leather', blurb: 'Stitched and worn in' },
  { id: 'chrome', name: 'Chrome', blurb: 'Mirror finish' },
  { id: 'plastic', name: 'Plastic', blurb: 'The red one from the box' },
  { id: 'none', name: 'No cup', blurb: 'Just the dice' },
];

export type MotionId = 'system' | 'full' | 'reduced';
export const MOTION: Option<MotionId>[] = [
  { id: 'system', name: 'Follow system', blurb: 'Respect the OS setting' },
  { id: 'full', name: 'Full', blurb: 'All the flourishes' },
  { id: 'reduced', name: 'Reduced', blurb: 'Cut the movement' },
];

/** Player accent colors (token names resolved in CSS as --player-<id>). */
export type PlayerColorId =
  'cyan' | 'magenta' | 'lime' | 'amber' | 'violet' | 'coral';
export const PLAYER_COLORS: Option<PlayerColorId>[] = [
  { id: 'cyan', name: 'Cyan', blurb: '' },
  { id: 'magenta', name: 'Magenta', blurb: '' },
  { id: 'lime', name: 'Lime', blurb: '' },
  { id: 'amber', name: 'Amber', blurb: '' },
  { id: 'violet', name: 'Violet', blurb: '' },
  { id: 'coral', name: 'Coral', blurb: '' },
];
